import React, { useState, useMemo } from 'react';
import { Search, Star, CheckCircle, XCircle, Edit, EyeOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';
import { useAdminData } from './AdminContext';
import { formatDateTime } from './utils';

const AdminReviewsTab = () => {
  const { reviews, selectedReviewFilter, setSelectedReviewFilter, approveReview, editReview, hideReview } = useAdminData();
  
  const [reviewsSearch, setReviewsSearch] = useState('');
  const [editingReview, setEditingReview] = useState(null);
  const [editedComment, setEditedComment] = useState('');

  const filteredReviews = useMemo(() => {
    return reviews.filter(r => {
      if (reviewsSearch === '') return true;
      const searchLower = reviewsSearch.toLowerCase();
      return (
        r.customer_name?.toLowerCase().includes(searchLower) ||
        r.vendor_name?.toLowerCase().includes(searchLower) ||
        r.comment?.toLowerCase().includes(searchLower)
      );
    });
  }, [reviews, reviewsSearch]);

  const renderStars = (rating) => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  const handleSaveEdit = async () => {
    await editReview(editingReview.id, editedComment);
    setEditingReview(null);
    setEditedComment('');
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Review Management</CardTitle>
          <CardDescription>Approve, edit, or remove customer reviews</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by customer, vendor, or comment..."
                value={reviewsSearch}
                onChange={(e) => setReviewsSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedReviewFilter} onValueChange={setSelectedReviewFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reviews</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reviews Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Comment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {reviewsSearch ? 'No reviews match your search' : 'No reviews found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReviews.map((review) => (
                    <TableRow key={review.id}>
                      <TableCell className="font-medium">{review.customer_name}</TableCell>
                      <TableCell className="text-gray-600">{review.vendor_name}</TableCell>
                      <TableCell>{renderStars(review.rating)}</TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600 max-w-xs truncate" title={review.comment}>
                          {review.comment}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          review.is_approved === true ? 'bg-green-100 text-green-800' :
                          review.is_approved === false ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>
                          {review.is_approved === true ? 'Approved' :
                           review.is_approved === false ? 'Rejected' : 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDateTime(review.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {review.is_approved !== true && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => approveReview(review.id, true)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          )}
                          {review.is_approved !== false && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => approveReview(review.id, false)}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditingReview(review); setEditedComment(review.comment); }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => hideReview(review.id)}
                          >
                            <EyeOff className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Review Dialog */}
      {editingReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Edit Review</CardTitle>
              <CardDescription>Edit and approve this review</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{editingReview.customer_name}</span>
                  {renderStars(editingReview.rating)}
                </div>
                <p className="text-sm text-gray-500">For: {editingReview.vendor_name}</p>
              </div>
              <div>
                <Label>Review Comment</Label>
                <Textarea
                  value={editedComment}
                  onChange={(e) => setEditedComment(e.target.value)}
                  rows={4}
                  className="mt-2"
                />
              </div>
              <div className="flex space-x-2">
                <Button onClick={handleSaveEdit} className="flex-1">
                  Save & Approve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setEditingReview(null); setEditedComment(''); }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default AdminReviewsTab;
